import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withLineBreaks } from './text';
import './Chat.css';

/**
 * Locates the substrings the teacher flagged as incorrect (annotation.correct === false)
 * as ranges over `text`. Without an explicit range we find each flagged element by its
 * first case-insensitive occurrence, mirroring the backend's addPositions(); an element
 * that does not occur at all yields no range.
 *
 * Overlapping ranges are merged so they never produce nested marks, which means one range
 * can answer to several annotations. Each therefore reports every `indices` it covers, as
 * positions in the unfiltered `annotations` array — the index a discussion's annotation
 * pointer is built from (see TeacherService.buildStudentMessageContext).
 *
 * @returns {Array<{start: number, end: number, indices: number[]}>} ordered by `start`.
 */
export function incorrectRanges(text, annotations) {
  const lower = text.toLowerCase();
  const ranges = [];
  (annotations || []).forEach((a, index) => {
    if (!a || a.correct !== false || !a.element) {
      return;
    }
    const start = lower.indexOf(a.element.toLowerCase());
    if (start >= 0) {
      ranges.push({ start, end: start + a.element.length, indices: [index] });
    }
  });

  ranges.sort((x, y) => x.start - y.start);
  const merged = [];
  ranges.forEach((r) => {
    const last = merged[merged.length - 1];
    if (last && r.start <= last.end) {
      last.end = Math.max(last.end, r.end);
      last.indices.push(...r.indices);
    } else {
      merged.push({ ...r, indices: [...r.indices] });
    }
  });
  return merged;
}

// Joins the explanations of every annotation a range covers, for the mark's tooltip.
function rangeTooltip(range, annotations) {
  return range.indices
    .map((i) => annotations[i].explanation)
    .filter(Boolean)
    .join('\n');
}

// Renders the message text with each incorrect range wrapped in a red <mark>. The mark is
// only the highlight; a style-reset <button> inside it carries the click, so a flagged word
// is reachable by keyboard and announced as an action. `onDiscuss` receives the position of
// the clicked range and the raw annotation indices it covers. Without it the mark carries no
// button: nothing would answer the click.
//
// `renderChooser(rangeIndex)` may return an element to mount inside that range's mark,
// which is what anchors the disambiguation popup to the very word it disambiguates.
function renderHighlighted(text, annotations, onDiscuss, t, renderChooser) {
  const merged = incorrectRanges(text, annotations);
  if (merged.length === 0) {
    return withLineBreaks(text);
  }

  const nodes = [];
  let cursor = 0;
  merged.forEach((r, i) => {
    if (cursor < r.start) {
      nodes.push(withLineBreaks(text.slice(cursor, r.start), `pre-${i}`));
    }
    const element = text.slice(r.start, r.end);
    const label = withLineBreaks(element, `mark-${i}`);
    nodes.push(
      <mark key={`hl-${i}`} className="incorrect" title={rangeTooltip(r, annotations) || undefined}>
        {onDiscuss ? (
          <button
            type="button"
            className="incorrect-mark-button"
            aria-label={t('message.discussElement', { element })}
            onClick={() => onDiscuss(i, r.indices)}
          >
            {label}
          </button>
        ) : (
          label
        )}
        {renderChooser(i)}
      </mark>
    );
    cursor = r.end;
  });
  if (cursor < text.length) {
    nodes.push(withLineBreaks(text.slice(cursor), 'post'));
  }
  return nodes;
}

// Lets the student pick which annotation to discuss when one mark covers several of them,
// which happens when two flagged elements overlap in the message text. It mounts inside the
// <mark> it belongs to, so it pops up against that word rather than against the bubble; a
// <span> because <mark> is phrasing content and may not hold a <div>.
function AnnotationChooser({ annotations, indices, onPick, onDismiss }) {
  const ref = useRef(null);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onDismiss();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [onDismiss]);

  return (
    <span className="message-menu-popdown annotation-chooser" role="menu" ref={ref}>
      {indices.map((index) => (
        <button
          key={index}
          type="button"
          role="menuitem"
          className="message-menu-item"
          title={annotations[index].explanation || undefined}
          onClick={() => onPick(index)}
        >
          {`✗ ${annotations[index].grammarRule}`}
        </button>
      ))}
    </span>
  );
}

// One turn of the conversation, as it was said and as the teacher read it.
function TranscriptMessage({ message, selected, onDiscussAnnotation, renderAside }) {
  const { t } = useTranslation();
  // The mark whose chooser is open, as {rangeIndex, indices}: its position among the
  // highlighted ranges, and the annotations it covers. Null when no chooser is open. The
  // position is what lets the popup mount inside the clicked mark rather than a twin of it.
  const [chooser, setChooser] = useState(null);
  // An interrupted spoken answer is one string cut in two: what came out of the speakers, and
  // what the model had written but never got to say.
  const heard = message.skipped == null ? message.text : message.text.slice(0, message.skipped);
  const unheard = message.skipped == null ? '' : message.text.slice(message.skipped);

  // A mark covering a single annotation opens its discussion straight away; one covering
  // several cannot name which, so it asks first.
  const handleDiscussMark = (rangeIndex, indices) => {
    if (indices.length === 1) {
      onDiscussAnnotation(message.id, indices[0]);
    } else {
      setChooser({ rangeIndex, indices });
    }
  };

  const dismissChooser = useCallback(() => setChooser(null), []);

  // Mounted inside the mark whose range is open, and nowhere else.
  const renderChooser = (rangeIndex) =>
    chooser && chooser.rangeIndex === rangeIndex ? (
      <AnnotationChooser
        annotations={message.annotations}
        indices={chooser.indices}
        onPick={(index) => {
          setChooser(null);
          onDiscussAnnotation(message.id, index);
        }}
        onDismiss={dismissChooser}
      />
    ) : null;

  return (
    <div className={`message ${message.sender}${selected ? ' selected' : ''}`}>
      <div className="message-content">
        {renderHighlighted(
          heard,
          message.annotations,
          onDiscussAnnotation ? handleDiscussMark : null,
          t,
          renderChooser,
        )}
        {/* What the model wrote after the student cut it off. Kept rather than dropped: the
            transcript shows what was missed instead of pretending it was never said. */}
        {unheard && <s className="message-unheard">{unheard}</s>}
      </div>
      {message.correction && (
        <div className="message-correction">{withLineBreaks(message.correction)}</div>
      )}
      {message.translation && (
        <div className="message-translation">{withLineBreaks(message.translation)}</div>
      )}
      {message.comment && (
        <div className="message-comment">💡 {withLineBreaks(message.comment)}</div>
      )}
      {message.timestamp && (
        <div className="message-timestamp">{message.timestamp.toLocaleTimeString()}</div>
      )}
      {/* Whatever the host hangs off this turn, unwrapped and last in the bubble. */}
      {renderAside?.(message)}
    </div>
  );
}

/**
 * An ordered conversation, drawn: the turns and nothing around them. The host frames them,
 * and must lay them out in a flex column — each bubble puts itself on its sender's side with
 * `align-self`, which does nothing outside one.
 *
 * @param messages the turns to draw, in order, each {id, text, sender: 'user' | 'persona',
 *   annotations, skipped, correction, translation, comment, timestamp}.
 * @param selectedIndex the position of the turn to single out, or null.
 * @param onDiscussAnnotation (messageId, annotationIndex) => void, opening a teacher discussion
 *   from a flagged word. Optional: without it the flagged words are marked but offer nothing.
 * @param renderAside (message) => node, what to put beside that turn, drawn last inside its
 *   bubble. Optional, and may answer nothing for a given turn: what hangs off a turn belongs to
 *   the screen showing it, not to the transcript.
 */
function Transcript({ messages, selectedIndex, onDiscussAnnotation, renderAside }) {
  return (
    <>
      {messages.map((message, index) => (
        <TranscriptMessage
          key={message.id != null ? `id-${message.id}` : `index-${index}`}
          message={message}
          selected={selectedIndex === index}
          onDiscussAnnotation={onDiscussAnnotation}
          renderAside={renderAside}
        />
      ))}
    </>
  );
}

export default Transcript;
